import React from "react";
import Box from "@material-ui/core/Box";
import CloseIcon from "@material-ui/icons/Close";
import FilterListIcon from "@material-ui/icons/FilterList";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { DateTime } from "luxon";

import { LocalizedButton, LocalizedIconButton } from "../../Button";
import { DatePager } from "../../DatePager";
import { LocalizedAutocompleteFilter, LocalizedFilter } from "../../Filter";

import { FilterBarProps } from "./interfaces";
import { useTranslation } from "../../../hooks/i18n";
import { useStyles } from ".";
import {
  AccountApplicableFilters,
  useEventApi,
} from "../../../hooks/use-event";
import { filterEventTypes } from "../../EventFilters/utils";
import { severities } from "../../../constants";

/**
 * Toolbar view
 * Contains filters for the media list
 *
 * This component is entirely "controlled", meaning that
 * it doesn't hold state about the current filters - its
 * parent is responsible for passing these back in on
 * each occurrence of onFilter.
 *
 * This makes it easy to load the component with an initial
 * set of filters (e.g. when deeplinking)
 *
 * @see https://reactjs.org/docs/forms.html#controlled-components
 */
export const MediaFilterBar: React.FC<FilterBarProps> = ({
  vehicles,
  users,
  onFilter,
  filters,
}) => {
  const classes = useStyles();
  const [showMobileFilters, setShowMobileFilters] = React.useState(false);
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (filter: string, values: string[]) => {
    const newFilters = {
      ...filters,
      [filter]: values,
    };
    onFilter(newFilters);
  };

  const handleDateChange = (value: string[]) => {
    const startDate = value[0];
    let endDate = value[1];
    if (!endDate) {
      endDate = DateTime.fromISO(startDate).endOf("day").toISO();
    }

    endDate = DateTime.fromISO(endDate).endOf("day").toISO();

    handleChange("date", [startDate, endDate]);
  };

  const { getAccountApplicableFilters } = useEventApi();

  const [
    applicableFilters,
    setApplicableFilters,
  ] = React.useState<AccountApplicableFilters>();

  React.useEffect(() => {
    const fetchFilters = async () => {
      const filters = await getAccountApplicableFilters();
      setApplicableFilters(filters);
    };
    fetchFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this once
  }, []);

  const filteredTypes = React.useMemo(
    () => filterEventTypes(false, applicableFilters),
    [applicableFilters]
  );

  return (
    <div>
      {isMobile && (
        <LocalizedButton
          color="primary"
          variant="text"
          onClick={() => setShowMobileFilters(true)}
          startIcon={<FilterListIcon />}
        >
          show_filters
        </LocalizedButton>
      )}
      {!isMobile || showMobileFilters ? (
        <div className={isMobile ? classes.popup : classes.toolbar}>
          {isMobile && (
            <LocalizedIconButton
              aria-label="close"
              className={classes.popupClose}
              onClick={() => setShowMobileFilters(false)}
            >
              <CloseIcon />
            </LocalizedIconButton>
          )}
          <Box>
            <DatePager onChange={handleDateChange} dates={filters.date} />
          </Box>
          <Box className={classes.filterWrap}>
            <LocalizedFilter
              id="event-type"
              label={t("type")}
              options={filteredTypes}
              onChange={(values) => {
                handleChange("type", values);
              }}
              value={filters.type}
            />
          </Box>
          <Box className={classes.filterWrap}>
            <LocalizedFilter
              id="event-severity"
              label={t("severity")}
              options={severities}
              onChange={(values) => {
                handleChange("severity", values);
              }}
              value={filters.severity}
            />
          </Box>
          {users && (
            <Box className={classes.vehicleFilterWrap}>
              <LocalizedAutocompleteFilter
                id="media-users"
                label={t("users")}
                options={users}
                onChange={(values) => {
                  handleChange("user", values);
                }}
                value={filters.user}
              />
            </Box>
          )}
          <Box className={classes.vehicleFilterWrap}>
            <LocalizedAutocompleteFilter
              id="media-vehicles"
              label={t("vehicles")}
              options={vehicles}
              onChange={(values) => {
                handleChange("vehicle", values);
              }}
              value={filters.vehicle}
            />
          </Box>

          {isMobile && (
            <LocalizedButton
              onClick={() => setShowMobileFilters(false)}
              fullWidth
            >
              apply_filters
            </LocalizedButton>
          )}
        </div>
      ) : null}
    </div>
  );
};
